import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, IconButton, useTheme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { DtDialog } from "../dialog/DtDialog";

interface DtViewableTextProps {
    value: string;
    previewLength?: number;
    sx?: object;
    codeStyle?: boolean;
    maxWidth?: number | string;
    fontSize?: string | number;
    fontFamily?: string;
    color?: string;
    background?: string;
    border?: string;
    modalTitle?: string;
    viewContent?: React.ReactNode;
}

const DtViewableText: React.FC<DtViewableTextProps> = ({
    value,
    previewLength,
    sx,
    codeStyle = false,
    maxWidth = '100%',
    fontSize = "0.95rem",
    fontFamily,
    color,
    background,
    border,
    modalTitle = "View Content",
    viewContent 
}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement | null>(null);

    const hasExplicitPreview = previewLength !== undefined;
    const effectivePreviewLength = previewLength ?? 80;

    const exceedsPreview = hasExplicitPreview && value.length > effectivePreviewLength;
    const preview = exceedsPreview ? value.slice(0, effectivePreviewLength) + "..." : value;

    useEffect(() => {
        if (!textRef.current || hasExplicitPreview) return;

        const el = textRef.current;

        const checkOverflow = () => {
            // Add 1px tolerance to account for sub-pixel rendering differences and browser rounding errors
            const overflowing = el.scrollHeight > el.clientHeight + 1;
            setIsOverflowing(overflowing);
        };

        checkOverflow();

        // React to resize / font changes
        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        resizeObserver.observe(el);

        return () => {
            resizeObserver.disconnect();
        };
    }, [value, fontSize, hasExplicitPreview]);

    const shouldShowViewButton = hasExplicitPreview ? exceedsPreview : isOverflowing;

    return (
        <>
            <Box sx={{ position: "relative", maxWidth, ...sx, display: "flex", alignItems: "center" }}>
                <Typography
                    component="span"
                    ref={textRef}
                    variant="body1"
                    sx={{
                        fontSize,
                        background: background,
                        wordBreak: "break-all",
                        whiteSpace: "pre-line",
                        border: border,
                        color: color ?? theme.palette.text.primary,
                        fontFamily: fontFamily ?? (codeStyle ? "monospace" : "inherit"),
                        flex: 1,
                        overflow: "auto",
                        // AUTO ONE-LINE MODE (no explicit preview): clamp to 1 line
                        ...(hasExplicitPreview
                            ? {}
                            : {
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }),
                    }}
                    title={value}
                >
                    {hasExplicitPreview ? preview : value}
                </Typography>
                {shouldShowViewButton && (
                    <IconButton
                        aria-label="View full content"
                        size="small"
                        sx={{
                            ml: 1,
                            color: theme.palette.primary.main,
                        }}
                        onClick={() => setOpen(true)}
                    >
                        <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            <DtDialog
                open={open}
                onClose={() => setOpen(false)}
                title={modalTitle}
                maxWidth="sm"
                fullWidth
            >
                {viewContent ? (
                    viewContent
                ) : (
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize,
                            background: background,
                            wordBreak: "break-all",
                            whiteSpace: "pre-line",
                            border: border,
                            color: color ?? theme.palette.text.primary,
                            fontFamily: fontFamily ?? (codeStyle ? "monospace" : "inherit"),
                        }}
                    >
                        {value}
                    </Typography>
                )}
            </DtDialog>
        </>
    );
};

export { DtViewableText };